import { useState } from 'react';
import { IconX, IconFolders, IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import Btn from './ui/Btn';
import { api } from '../lib/api';
import { toast } from '../stores/toaster';

type Props = { dossierId: string; onSorted?: () => void };

type Proposal = {
  pdf_id: string;
  filename: string | null;
  category: string;
  confidence: number;
  proposed_bucket: 'piece_client' | 'piece_adverse' | null;
};

type AutoSortResult = {
  status: string;
  mode?: string;
  total?: number;
  proposals?: Proposal[];
  applied?: number;
  errors?: { pdf_id: string; error: string }[];
  error?: string;
};

type TaskStatusResponse = {
  task_id: string;
  state: string;
  ready: boolean;
  result?: AutoSortResult;
  info?: string;
};

type AsyncResp = { task_id: string; async: boolean; apply: boolean; poll_url: string };

type Decision = 'piece_client' | 'piece_adverse' | 'skip';

const MIN_CONF = 0.75;

const CAT_LABEL: Record<string, string> = {
  conclusions_client: 'Conclusions client',
  pv_audition: "PV d'audition",
  conclusions_adverse: 'Conclusions adverses',
  requisitoire: 'Réquisitoire',
  citation: 'Citation',
  jugement: 'Jugement',
  expertise: 'Expertise',
  autre: 'Autre',
  ignore: 'Ignorer',
};

async function pollTask(taskId: string): Promise<TaskStatusResponse> {
  const start = Date.now();
  const maxMs = 30 * 60 * 1000;
  while (Date.now() - start < maxMs) {
    const s = await api.get<TaskStatusResponse>(`/tasks/${taskId}/status`);
    if (s.ready) return s;
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error('timeout polling task');
}

/**
 * Bouton « Ranger tout » : classe les pièces du vrac (dossier répressif) via
 * IA et propose de les déplacer vers Pièces client / Pièces adverses. Flux en
 * deux temps : aperçu (apply=false) → ajustement par ligne → application.
 */
export default function AutoSortButton({ dossierId, onSorted }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [skippedCount, setSkippedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const openModal = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setProposals([]);
    try {
      const resp = await api.post<AsyncResp>(
        `/pieces-migration/dossier/${dossierId}/auto-sort`,
        { apply: false }
      );
      const final = await pollTask(resp.task_id);
      if (final.state !== 'SUCCESS' || final.result?.status !== 'ok') {
        setError(final.result?.error || final.info || final.state);
        return;
      }
      const all = final.result.proposals || [];
      const actionable = all.filter((p) => p.proposed_bucket);
      setSkippedCount(all.length - actionable.length);
      setProposals(actionable);
      const init: Record<string, Decision> = {};
      for (const p of actionable) {
        init[p.pdf_id] =
          p.confidence >= MIN_CONF && p.proposed_bucket ? p.proposed_bucket : 'skip';
      }
      setDecisions(init);
    } catch (e) {
      const err = e as { message?: string; problem?: { detail?: string } };
      setError(err?.message || err?.problem?.detail || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const applySort = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await api.post<AsyncResp>(
        `/pieces-migration/dossier/${dossierId}/auto-sort`,
        { apply: true, overrides: decisions }
      );
      const final = await pollTask(resp.task_id);
      if (final.state !== 'SUCCESS' || final.result?.status !== 'ok') {
        toast.error('Rangement KO : ' + (final.result?.error || final.info || final.state));
        return;
      }
      toast.success(`${final.result.applied ?? 0} pièce(s) rangée(s).`);
      setOpen(false);
      onSorted?.();
    } catch (e) {
      const err = e as { message?: string };
      toast.error('Rangement KO : ' + (err?.message || 'inconnu'));
    } finally {
      setBusy(false);
    }
  };

  const movable = proposals.filter(
    (p) => decisions[p.pdf_id] && decisions[p.pdf_id] !== 'skip'
  ).length;

  return (
    <>
      <Btn variant="ghost" size="sm" onClick={() => void openModal()}>
        <IconFolders size={15} className="mr-1" /> Ranger tout
      </Btn>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-lg bg-paper-elevated shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-rule px-5 py-3">
              <h2 className="font-serif text-lg">Ranger les pièces automatiquement</h2>
              <button
                onClick={() => !busy && setOpen(false)}
                className="text-text-tertiary hover:text-text-primary"
              >
                <IconX size={18} />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              {loading && (
                <p className="text-sm text-text-secondary">
                  Analyse des pièces en cours (IA)…
                </p>
              )}
              {error && (
                <p className="flex items-center gap-2 text-sm text-status-error">
                  <IconAlertTriangle size={16} /> {error}
                </p>
              )}
              {!loading && !error && proposals.length === 0 && (
                <p className="text-sm text-text-secondary">
                  Aucune pièce du vrac à reclasser (pièces client / adverses).
                </p>
              )}
              {!loading && proposals.length > 0 && (
                <>
                  <p className="mb-3 text-[13px] text-text-secondary">
                    L&apos;IA propose de déplacer ces pièces hors du dossier répressif.
                    Vérifiez et ajustez, puis appliquez.
                    {skippedCount > 0 &&
                      ` ${skippedCount} autre(s) pièce(s) resteront dans le dossier répressif.`}
                  </p>
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="border-b border-rule text-left text-text-tertiary">
                        <th className="py-1.5 pr-2 font-medium">Pièce</th>
                        <th className="py-1.5 pr-2 font-medium">Détecté</th>
                        <th className="py-1.5 font-medium">Ranger vers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposals.map((p) => (
                        <tr key={p.pdf_id} className="border-b border-rule/50 align-top">
                          <td className="py-1.5 pr-2">{p.filename || p.pdf_id}</td>
                          <td className="py-1.5 pr-2 text-text-secondary">
                            {CAT_LABEL[p.category] || p.category} ·{' '}
                            {(p.confidence * 100).toFixed(0)}%
                          </td>
                          <td className="py-1.5">
                            <select
                              value={decisions[p.pdf_id] ?? 'skip'}
                              onChange={(e) =>
                                setDecisions((d) => ({
                                  ...d,
                                  [p.pdf_id]: e.target.value as Decision,
                                }))
                              }
                              className="rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[13px] focus:border-accent focus:outline-none"
                            >
                              <option value="skip">Garder (dossier répressif)</option>
                              <option value="piece_client">Pièce client</option>
                              <option value="piece_adverse">Pièce adverse</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-rule px-5 py-3">
              <Btn variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={busy}>
                Annuler
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                onClick={() => void applySort()}
                disabled={busy || loading || movable === 0}
              >
                <IconCheck size={15} className="mr-1" />{' '}
                {busy ? 'Rangement…' : `Ranger ${movable} pièce(s)`}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
